import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { showProductAssorting } from "../erpClient";

import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

function ProductAssortingTable() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [tableData, setTableData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch all product assortings and their details in one API call
  const fetchAllProductData = async () => {
    setIsLoading(true);
    try {
      const response = await showProductAssorting();

      if (response?.success && response?.data?.core_products_data) {
        const coreProductsData = response.data.core_products_data;

        // Map the data to table format
        const mappedData = coreProductsData.map((product) => ({
          id: String(product.core_pro_id || ""),
          name: product.category_name || "",
          totalPcs: product.total_pcs || 0,
          metalGroupWisePcs: product.metal_group_wise_pcs || [],
        }));

        // Sort by ID in ascending order (oldest first)
        mappedData.sort((a, b) => {
          const idA = parseInt(a.id) || 0;
          const idB = parseInt(b.id) || 0;
          return idA - idB;
        });

        setTableData(mappedData);
      } else {
        setTableData([]);
        toast.error(response?.message || "No data found");
      }
    } catch (error) {
      console.error("Error fetching product assortings:", error);
      toast.error(
        error.response?.data?.message || "Failed to fetch product assortings"
      );
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProductData();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in ProductAssortingTable, refetching data...', event.detail);
      fetchAllProductData();
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, []);

  // Filter table data based on search query
  const filteredTableData = tableData.filter(
    (item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const totalItems = filteredTableData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredTableData.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">Product Assorting Table</h4>
            <Button
              variant="primary"
              className={buttonClass}
              onClick={() => navigate("/stock-check")}
            >
              Stock Check
            </Button>
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: "240px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                style={{ minWidth: "100%" }}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th style={{ width: "5%" }}>ID</th>
                    <th style={{ width: "15%" }}>Product Assorting Name</th>
                    <th style={{ width: "10%" }}>Total PCS</th>
                    <th style={{ width: "23%" }}>Silver</th>
                    <th style={{ width: "23%" }}>Gold</th>
                    <th style={{ width: "24%" }}>UCHHAK</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item) => {
                      // Filter metal groups by type
                      const silverItems = item.metalGroupWisePcs.filter((mg) =>
                        mg.group_name?.toLowerCase().includes("silver")
                      );
                      const goldItems = item.metalGroupWisePcs.filter(
                        (mg) =>
                          mg.group_name?.toLowerCase().includes("gold") ||
                          mg.group_name?.toLowerCase().includes("carat")
                      );
                      const uchhakItems = item.metalGroupWisePcs.filter(
                        (mg) =>
                          mg.group_name?.toLowerCase().includes("uchak") ||
                          mg.group_name?.toLowerCase().includes("uchhak")
                      );

                      return (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item.name}</td>
                          <td>
                            <div>
                              <span className="badge bg-primary text-white mb-2">
                                {item.totalPcs}
                              </span>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#6c757d",
                                  marginTop: "0.5rem",
                                  lineHeight: "1.4",
                                }}
                              >
                                {silverItems.length > 0 && (
                                  <div>
                                    <strong>Silver:</strong>{" "}
                                    {silverItems.reduce(
                                      (sum, mg) =>
                                        sum + (parseInt(mg.total_pcs) || 0),
                                      0
                                    )}
                                  </div>
                                )}
                                {goldItems.length > 0 && (
                                  <div>
                                    <strong>Gold:</strong>{" "}
                                    {goldItems.reduce(
                                      (sum, mg) =>
                                        sum + (parseInt(mg.total_pcs) || 0),
                                      0
                                    )}
                                  </div>
                                )}
                                {uchhakItems.length > 0 && (
                                  <div>
                                    <strong>UCHHAK:</strong>{" "}
                                    {uchhakItems.reduce(
                                      (sum, mg) =>
                                        sum + (parseInt(mg.total_pcs) || 0),
                                      0
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {/* Silver Column */}
                          <td>
                            {silverItems.length > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                }}
                              >
                                {silverItems.map((metalGroup, index) => (
                                  <span
                                    key={
                                      metalGroup.metal_group_id ||
                                      `silver-${index}`
                                    }
                                    className="badge bg-light text-dark"
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: "normal",
                                      padding: "0.5rem 0.75rem",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <strong>{metalGroup.group_name}:</strong>{" "}
                                    <span className="text-primary">
                                      {metalGroup.total_pcs} PCS
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          {/* Gold Column */}
                          <td>
                            {goldItems.length > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                }}
                              >
                                {goldItems.map((metalGroup, index) => (
                                  <span
                                    key={
                                      metalGroup.metal_group_id ||
                                      `gold-${index}`
                                    }
                                    className="badge bg-light text-dark"
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: "normal",
                                      padding: "0.5rem 0.75rem",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <strong>{metalGroup.group_name}:</strong>{" "}
                                    <span className="text-primary">
                                      {metalGroup.total_pcs} PCS
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          {/* UCHHAK Column */}
                          <td>
                            {uchhakItems.length > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                }}
                              >
                                {uchhakItems.map((metalGroup, index) => (
                                  <span
                                    key={
                                      metalGroup.metal_group_id ||
                                      `uchhak-${index}`
                                    }
                                    className="badge bg-light text-dark"
                                    style={{
                                      fontSize: "0.875rem",
                                      fontWeight: "normal",
                                      padding: "0.5rem 0.75rem",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <strong>{metalGroup.group_name}:</strong>{" "}
                                    <span className="text-primary">
                                      {metalGroup.total_pcs} PCS
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductAssortingTable;
